import {
  ExportCertificationIN,
  ExportCertificationOptions,
  LCAClassEnum,
  LevelOfRequirementsEnum,
  buildingsApi,
  getLocalAuthHeader,
} from "api-client";
import { makeAutoObservable } from "mobx";
import { BUILDING_TYPES } from "utils";
import { buildingStore } from "store/Building/BuildingStore";
import { conformityStore } from "../../store/ConformityStore.ts";

type AdditionalInfo = {
  buildingType: string;
  levelOfRequirements?: string;
  buildingClass?: string;
  energeticStandard?: string;
  relevantArea?: string;
  netFloorArea?: string;
  TGAFactor?: boolean;
};

type CertExporter = {
  additionalInfo: AdditionalInfo;
  saveSettings: boolean;
};

class CertificationExportStore {
  activeStep = 0;
  additionalInfo: AdditionalInfo = {
    buildingType: "",
    levelOfRequirements: "",
    buildingClass: "",
    energeticStandard: "",
    relevantArea: "",
    netFloorArea: "",
    TGAFactor: false,
  };
  certExporter: CertExporter = {
    additionalInfo: this.additionalInfo,
    saveSettings: true,
  };
  base_amounts_matrix_key = "";
  certification_id = "";
  certification_key = "";
  conformity_id = "";
  exportIsInProgress = false;

  setCertAndConformityId(certName: string) {
    if (!conformityStore.certificationOptions?.length) {
      throw Error("Certification options are not available");
    }

    const foundCert = conformityStore.certificationOptions.find(
      (co) => co.name === certName
    );

    this.setCertificationId(foundCert?.id ?? "");
    this.setCertificationKey(certName);
    this.setConformityId(foundCert?.conformities[0].id ?? "");
  }

  setCertificationId(certification_id: string) {
    this.certification_id = certification_id;
  }

  setCertificationKey(certification_key: string) {
    this.certification_key = certification_key;
  }

  setConformityId(conformity_id: string) {
    this.conformity_id = conformity_id;
  }

  setExportIsInProgress(exportIsInProgress: boolean) {
    this.exportIsInProgress = exportIsInProgress;
  }

  setAdditionalInfoFieldAndValue(
    field: keyof AdditionalInfo,
    value: string | undefined | boolean
  ) {
    this.additionalInfo = {
      ...this.additionalInfo,
      [field]: value,
    };
    this.certExporter = {
      ...this.certExporter,
      additionalInfo: this.additionalInfo,
    };
  }

  setCertExportFieldAndValue(
    field: keyof CertExporter,
    value: string | undefined | boolean
  ) {
    this.certExporter = {
      ...this.certExporter,
      [field]: value,
    };
  }

  handleOnDropdownChange(
    field: keyof AdditionalInfo,
    items: {
      id: string;
      name: string;
      name_de?: string;
    }[],
    value: string | undefined
  ) {
    if (value) {
      const selectedValue = items.find((ct) => ct.id === value);
      this.setAdditionalInfoFieldAndValue(
        field,
        selectedValue?.name ?? selectedValue?.name_de
      );
    }
  }

  setCertExporter(certExporter: CertExporter) {
    this.certExporter = certExporter;
  }

  setActiveStep(step: number) {
    this.activeStep = step;
  }

  resetData() {
    this.activeStep = 0;
    this.additionalInfo = {
      buildingClass: "",
      buildingType: "",
      energeticStandard: "",
      levelOfRequirements: "",
    };
    this.certExporter = {
      additionalInfo: this.additionalInfo,
      saveSettings: true,
    };
  }

  getBaseAmountMatrixKey() {
    if (this.isResidentialBuilding()) {
      this.base_amounts_matrix_key =
        this.additionalInfo.energeticStandard?.includes("> 40")
          ? "residential_over_40"
          : "residential_under_40";
      return;
    }

    const buildingClass = this.additionalInfo.buildingClass;
    if (this.additionalInfo.levelOfRequirements?.includes("PLUS")) {
      if (
        buildingClass?.includes("K1") ||
        buildingClass?.includes("K4") ||
        buildingClass?.includes("K5")
      ) {
        this.base_amounts_matrix_key = "non_residential_k1_k4_k5_plus";
      } else if (
        buildingClass?.includes("K2") ||
        buildingClass?.includes("K3")
      ) {
        this.base_amounts_matrix_key = "non_residential_k2_k3_plus";
      } else {
        this.base_amounts_matrix_key = "non_residential_k6_plus";
      }
    } else if (
      buildingClass?.includes("K1") ||
      buildingClass?.includes("K4") ||
      buildingClass?.includes("K5")
    ) {
      this.base_amounts_matrix_key = "non_residential_k1_k4_k5_premium";
    } else if (buildingClass?.includes("K2") || buildingClass?.includes("K3")) {
      this.base_amounts_matrix_key = "non_residential_k2_k3_premium";
    } else {
      this.base_amounts_matrix_key = "non_residential_k6_premium";
    }
  }

  private isResidentialBuilding() {
    return (
      BUILDING_TYPES[0].name === this.additionalInfo.buildingType ||
      BUILDING_TYPES[0].name_de === this.additionalInfo.buildingType
    );
  }

  handleErrors(bId: string) {
    if (!bId) {
      throw Error("Building id is not available!");
    }

    if (!this.certification_id) {
      throw Error("Certification id not found!");
    }

    if (!this.conformity_id) {
      throw Error("Conformity id not found!");
    }
  }

  async createCertificationExporterFromApi(buildingId: string) {
    let options: ExportCertificationOptions | null = null;
    if (!buildingId || !this.certification_id || !this.conformity_id) {
      return this.handleErrors(buildingId);
    }
    if (this.certification_key?.includes("2018")) {
      options = this.getDGNB18Options();
    } else if (
      this.certification_key?.includes("2023") ||
      this.certification_key?.includes("QNG")
    ) {
      options = this.getDGNB23OrQNGOptions();
    } else if (this.certification_key?.includes("BNB")) {
      options = this.getBNBOptions();
    }

    const exportCertificationIN: ExportCertificationIN = {
      certification_id: this.certification_id,
      conformity_id: this.conformity_id,
      options: options,
      save_settings: this.certExporter.saveSettings,
    };

    const authHeader = getLocalAuthHeader();
    return await buildingsApi
      .concularApiV1RoutersBuildingsExportCertification(
        buildingId,
        exportCertificationIN,
        authHeader
      )
      .then(async (res) => {
        if (this.certExporter.saveSettings) {
          const res = await buildingStore.getCurrentBuildingDetails();
          res && buildingStore.setCurrentBuilding(res);
        }
        return res;
      });
  }

  private getBNBOptions() {
    const options = {
      net_floor_area: Number(this.additionalInfo.netFloorArea) || null,
      is_active:
        buildingStore.currentBuilding?.certification_export_settings?.bnb
          ?.is_active ?? false,
    } as ExportCertificationOptions;
    return options;
  }

  private getDGNB23OrQNGOptions() {
    let options: ExportCertificationOptions | null = null;
    const isActiveInSettings = this.certification_key.includes("2023")
      ? buildingStore.currentBuilding?.certification_export_settings?.dgnb23
          ?.is_active
      : buildingStore.currentBuilding?.certification_export_settings?.qng
          ?.is_active;

    if (this.isResidentialBuilding()) {
      options = {
        building_type: this.additionalInfo.buildingType,
        energetic_standard: this.additionalInfo.energeticStandard || null,
        is_active: isActiveInSettings,
      };
    } else {
      options = {
        level_of_requirements:
          (this.additionalInfo
            .levelOfRequirements as LevelOfRequirementsEnum) || null,
        lca_class: (this.additionalInfo.buildingClass as LCAClassEnum) || null,
        building_type: this.additionalInfo.buildingType,
        is_active: isActiveInSettings ?? false,
      };
    }
    this.getBaseAmountMatrixKey();
    options.base_amounts_matrix_key = this.base_amounts_matrix_key;

    return options;
  }

  private getDGNB18Options() {
    const options = {
      reduce_tga_factor_to_1_1: this.additionalInfo.TGAFactor ?? false,
      relevant_area: Number(this.additionalInfo.relevantArea) || null,
      is_active:
        buildingStore.currentBuilding?.certification_export_settings?.dgnb18
          ?.is_active ?? false,
    } as ExportCertificationOptions;
    return options;
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: CertificationExportStore;

  static getInstance(): CertificationExportStore {
    if (!CertificationExportStore.instance) {
      CertificationExportStore.instance = new CertificationExportStore();
    }
    return CertificationExportStore.instance;
  }
}

export const certificationExportStore = CertificationExportStore.getInstance();
